import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllMemories } from "@/services/firebase/memory-functions";
import { MemoryModel, formatLocation, formatDisplayDate, DateParser } from "common247";
import { MemoryCard } from "@/components/memory/MemoryCard";
import { formatFavouriteRatingGroup } from "@/components/media/MediaGallery";

const MEMORY_SORT_OPTIONS: ISortOption<MemoryModel>[] = [
  { key: "date", label: "Event Date", getValue: (m) => formatDisplayDate(m.date) },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
  { key: "title", label: "Title", getValue: (m) => m.title || "" },
  { key: "createdAt", label: "Created Date", getValue: (m) => m.createdAt || 0 },
  { key: "location", label: "Location", getValue: (m) => formatLocation(m.location) },
  { key: "owner", label: "Owner", getValue: (m) => m.ownerDisplayName || "" },
];

const MEMORY_GROUP_OPTIONS: IGroupOption<MemoryModel>[] = [
  {
    key: "favouriteRating",
    label: "Favourite Rating",
    getGroupValue: (m) => formatFavouriteRatingGroup(m.favouriteRating),
  },
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (m) => m.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "year",
    label: "Year",
    getGroupValue: (m) => {
      if (m.date) {
        const parsed = DateParser.parse(m.date);
        if (parsed.from?.year) return String(parsed.from.year);
      }
      return m.createdAt ? String(new Date(m.createdAt).getFullYear()) : "No Date";
    },
  },
  {
    key: "location",
    label: "Location",
    getGroupValue: (m) => formatLocation(m.location) || "No Location Specified",
  },
];

/**
 * Administrative screen displaying all platform memories with view, sort, group, and filter capabilities.
 */
export default function MemoryAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [memories, setMemories] = useState<MemoryModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllMemories(services, {});
      if (response.status) {
        setMemories(response.memories || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to fetch memories.");
      }
    } catch (error) {
      console.error("Error fetching memories:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching memories.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  return (
    <ThemedScreen title="Memory Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${memories.length} TOTAL ${memories.length === 1 ? "MEMORY" : "MEMORIES"}`}
        </ThemedText>

        <ThemedList<MemoryModel>
          data={memories}
          sortOptions={MEMORY_SORT_OPTIONS}
          defaultSortKey="date"
          defaultSortDirection="desc"
          groupOptions={MEMORY_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-memories"
          pageSize={24}
          filterKeys={["title", "description", "location", "ownerDisplayName"]}
          filterPlaceholder="Search all memories..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No memories found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <MemoryCard
              memory={item}
              showOwner={true}
              isAdminView={true}
              canEditTags={false}
              viewMode={viewMode}
              onPress={() => {}}
            />
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 12,
  },
});
